import { buildCharacterActionEconomyViewModel } from './CharacterActionEconomyPresentation';
import {
  buildCharacterInventoryRows,
  buildCharacterProficiencySummary,
  buildCharacterWealthSummary,
  hasDistinctStructuredNotes
} from './CharacterSheetReferencePresentation';
import { buildCharacterWeaponTableEntries } from './CharacterSheetWeaponPresentation';
import {
  collectCharacterEquipment,
  resolveCharacterArmorClass,
  resolveCharacterChoiceSummary,
  resolveCharacterDefensesAndSenses,
  resolveCharacterHitDice
} from './CharacterRulesPresentation';
import { enrichSpellActionData } from './combat/SpellActionReference';

const ABILITY_GROUPS = Object.freeze([
  { key: 'strength', label: 'Strength', shortLabel: 'STR', skills: ['Athletics'] },
  { key: 'dexterity', label: 'Dexterity', shortLabel: 'DEX', skills: ['Acrobatics', 'Sleight of Hand', 'Stealth'] },
  { key: 'constitution', label: 'Constitution', shortLabel: 'CON', skills: [] },
  { key: 'intelligence', label: 'Intelligence', shortLabel: 'INT', skills: ['Arcana', 'History', 'Investigation', 'Nature', 'Religion'] },
  { key: 'wisdom', label: 'Wisdom', shortLabel: 'WIS', skills: ['Animal Handling', 'Insight', 'Medicine', 'Perception', 'Survival'] },
  { key: 'charisma', label: 'Charisma', shortLabel: 'CHA', skills: ['Deception', 'Intimidation', 'Performance', 'Persuasion'] }
]);

const LOADOUT_SLOT_DEFINITIONS = Object.freeze([
  { key: 'head', marker: 1, label: 'Head and Neck' },
  { key: 'body', marker: 2, label: 'Body and Armor' },
  { key: 'back', marker: 3, label: 'Back and Shoulders' },
  { key: 'hands', marker: 4, label: 'Hands and Held' },
  { key: 'waist', marker: 5, label: 'Waist and Accessories' },
  { key: 'feet', marker: 6, label: 'Feet' }
]);

const asArray = (value) => (Array.isArray(value) ? value : []);

const toFiniteNumber = (value, fallback = null) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return '';
};

const humanize = (value, fallback = '') => {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatSpeed = (value) => {
  const text = firstText(value, 30);
  return /\b(?:ft\.?|feet)\b/i.test(text) ? text : `${text} ft.`;
};

export const getCharacterAbilityModifier = (score) => {
  const numeric = toFiniteNumber(score, 10);
  return Math.floor((numeric - 10) / 2);
};

export const formatCharacterSignedNumber = (value) => {
  const numeric = toFiniteNumber(value, 0);
  return numeric >= 0 ? `+${numeric}` : String(numeric);
};

export const getCharacterProficiencyBonus = (character) => {
  const explicit = toFiniteNumber(character?.proficiencyBonus);
  if (explicit != null) return explicit;
  const level = Math.max(1, toFiniteNumber(character?.level, 1));
  return Math.floor((level - 1) / 4) + 2;
};

export const resolveCharacterPrintSkill = (character, skillName, abilityName) => {
  const skillsMap = character?.skills && typeof character.skills === 'object' && !Array.isArray(character.skills)
    ? character.skills
    : null;
  const mapEntry = skillsMap ? skillsMap[skillName] : null;
  const detailed = asArray(character?.skillsDetailed).find(
    (entry) => String(entry?.name || '').toLowerCase() === String(skillName).toLowerCase()
  );
  const baseModifier = getCharacterAbilityModifier(character?.abilities?.[abilityName]);
  const proficiencyBonus = getCharacterProficiencyBonus(character);
  const storedBonus = toFiniteNumber(detailed?.bonus ?? mapEntry?.value ?? mapEntry?.bonus);
  const legacyProficient = Array.isArray(character?.skills)
    && character.skills.some((entry) => String(entry).toLowerCase() === String(skillName).toLowerCase());
  const schemaTraining = typeof mapEntry === 'string' ? mapEntry.toLowerCase() : '';
  const proficient = Boolean(
    mapEntry?.proficient === true
    || detailed?.proficient === true
    || legacyProficient
    || schemaTraining === 'proficient'
    || schemaTraining === 'expert'
    || schemaTraining === 'half-proficient'
    || (storedBonus != null && storedBonus !== baseModifier)
  );
  const expertise = Boolean(
    mapEntry?.expertise === true
    || detailed?.expertise === true
    || schemaTraining === 'expert'
  );

  return {
    name: skillName,
    proficient,
    expertise,
    value: baseModifier + (proficient ? proficiencyBonus : 0) + (expertise ? proficiencyBonus : 0)
  };
};

const resolveSavingThrow = (character, abilityKey) => {
  const raw = character?.savingThrows?.[abilityKey];
  const baseModifier = getCharacterAbilityModifier(character?.abilities?.[abilityKey]);
  const numeric = toFiniteNumber(raw);
  const proficient = typeof raw === 'boolean'
    ? raw
    : (numeric != null ? numeric !== baseModifier : false);
  return {
    proficient,
    value: baseModifier + (proficient ? getCharacterProficiencyBonus(character) : 0)
  };
};

const normalizeReferenceEntry = (entry, index, fallbackName) => {
  if (typeof entry === 'string') {
    const text = entry.trim();
    if (!text) return null;
    return { key: `${fallbackName}-${index}-${text.toLowerCase()}`, name: text, description: '' };
  }
  if (!entry || typeof entry !== 'object') return null;
  const name = firstText(entry.name, entry.title, entry.label, fallbackName)
    .replace(/mage hand legerdemaine/gi, 'Mage Hand Legerdemain');
  const rawDescription = firstText(entry.description, entry.text, entry.content, entry.summary, entry.effect);
  const description = /^(?:no description available|no description|n\/a)$/i.test(rawDescription) ? '' : rawDescription
    .replace(/mage hand legerdemaine/gi, 'Mage Hand Legerdemain')
    .replace(/\bShe wears\b/g, 'She wears');
  if (!name && !description) return null;
  return {
    key: String(entry.id || `${fallbackName}-${index}-${name.toLowerCase()}`),
    name: name || fallbackName,
    description,
    source: /starter character generator/i.test(firstText(entry.source?.name, entry.source))
      ? ''
      : firstText(entry.source?.name, entry.source),
    uses: entry.uses || null
  };
};

const normalizeReferenceList = (entries, fallbackName) => asArray(entries)
  .map((entry, index) => normalizeReferenceEntry(entry, index, fallbackName))
  .filter((entry) => entry && !/starter character generator/i.test(entry.name));

const enrichReferenceMechanics = (entries, character) => {
  const dexterity = getCharacterAbilityModifier(character?.abilities?.dexterity);
  const dc = 8 + dexterity + getCharacterProficiencyBonus(character);
  const hasPoisonersKit = collectCharacterEquipment(character)
    .some((item) => /poisoner'?s kit/i.test(String(item?.name || '')));
  return entries.map((entry) => {
    if (/^cunning strike$/i.test(entry.name)) {
      return {
        ...entry,
        description: `When Sneak Attack would deal 3d6, choose an option and reduce it to 2d6. Save DC ${dc}. Poison: ${hasPoisonersKit ? 'available with the Poisoner’s Kit; the target repeats the save at the end of each turn.' : 'unavailable until a Poisoner’s Kit is carried.'} Withdraw: move up to 15 ft. without provoking Opportunity Attacks.`
      };
    }
    return entry;
  });
};

const formatClassLine = (character) => {
  const rawClasses = asArray(character?.classes);
  const classes = rawClasses
    .map((entry) => {
      const name = firstText(entry?.name, entry?.className, entry?.class);
      const level = toFiniteNumber(entry?.level);
      const subclass = firstText(entry?.subclass, entry?.subclassName, rawClasses.length === 1 ? character?.subclass : '', rawClasses.length === 1 ? character?.subclassName : '');
      if (!name) return '';
      return `${name}${subclass ? ` (${subclass})` : ''}${level != null ? ` ${level}` : ''}`;
    })
    .filter(Boolean);
  if (classes.length > 0) return classes.join(' / ');
  const name = firstText(character?.class, 'Adventurer');
  const subclass = firstText(character?.subclass, character?.subclassName);
  return `${name}${subclass ? ` (${subclass})` : ''}`;
};

const normalizeClassProgression = (character) => {
  const recorded = asArray(character?.classes)
    .map((entry, index) => {
      const name = firstText(entry?.name, entry?.className, entry?.class);
      if (!name) return null;
      return {
        key: String(entry?.id || `class-${index}-${name.toLowerCase()}`),
        name,
        subclass: firstText(entry?.subclass, entry?.subclassName, asArray(character?.classes).length === 1 ? character?.subclass : '', asArray(character?.classes).length === 1 ? character?.subclassName : ''),
        level: Math.max(1, toFiniteNumber(entry?.level, 1)),
        hitDie: firstText(entry?.hitDie, entry?.hitDice, entry?.hitDiceType),
        spellcasting: firstText(entry?.spellcastingProgression, entry?.spellcasting, entry?.castingType),
        notes: firstText(entry?.notes)
      };
    })
    .filter(Boolean);
  if (recorded.length > 0) return recorded;

  return [{
    key: 'primary-class',
    name: firstText(character?.class, 'Adventurer'),
    subclass: firstText(character?.subclass, character?.subclassName),
    level: Math.max(1, toFiniteNumber(character?.level, 1)),
    hitDie: firstText(character?.hitDie, character?.hitDiceType),
    spellcasting: '',
    notes: ''
  }];
};

const normalizeSpellRows = (spellData, character) => asArray(spellData?.entries)
  .map((spell, index) => {
    spell = enrichSpellActionData(spell, { subject: character });
    const name = firstText(spell?.name, spell?.title, spell?.label);
    if (!name) return null;
    const level = Math.max(0, toFiniteNumber(spell?.level ?? spell?.spellLevel, 0));
    const components = typeof spell?.components === 'string'
      ? spell.components
      : [spell?.verbal ? 'V' : '', spell?.somatic ? 'S' : '', spell?.material || spell?.materialComponent ? 'M' : '']
        .filter(Boolean)
        .join(', ');
    return {
      key: String(spell?.id || `spell-${level}-${index}-${name.toLowerCase()}`),
      level,
      levelLabel: level === 0 ? 'Cantrip' : String(level),
      prepared: spell?.prepared === true,
      ritual: spell?.ritual === true,
      concentration: spell?.concentration === true,
      name,
      castingTime: firstText(spell?.castingTime, spell?.castTime),
      range: firstText(spell?.range, spell?.spellRange),
      components,
      material: firstText(spell?.material, spell?.materialComponent),
      duration: firstText(spell?.duration),
      description: firstText(spell?.description, spell?.text, spell?.summary, spell?.effect),
      acquisition: firstText(spell?.acquisition, spell?.grantedBy, spell?.source?.name, spell?.source),
      modification: firstText(spell?.modification),
      attackBonus: toFiniteNumber(spell?.attackBonus ?? spell?.attack?.toHit),
      damage: asArray(spell?.damage)
        .map((entry) => `${firstText(entry?.dice, entry?.formula)} ${firstText(entry?.type, entry?.damageType)}`.trim())
        .filter(Boolean)
        .join(', ')
    };
  })
  .filter(Boolean)
  .sort((left, right) => left.level - right.level || left.name.localeCompare(right.name));

const normalizeSpellSlots = (spellData) => Object.entries(spellData?.slots || {})
  .map(([level, slot]) => ({
    level: Number(level),
    total: Math.max(0, toFiniteNumber(slot?.total ?? slot?.max, 0)),
    used: Math.max(0, toFiniteNumber(slot?.used, 0))
  }))
  .filter((slot) => Number.isFinite(slot.level) && slot.level > 0 && (slot.total > 0 || slot.used > 0))
  .sort((left, right) => left.level - right.level);

const makeNarrativeField = (label, ...values) => {
  const value = firstText(...values);
  return value ? { label, value } : null;
};

const makeNarrativeGroup = (title, fields) => {
  const visibleFields = fields.filter(Boolean);
  return visibleFields.length > 0 ? { title, fields: visibleFields } : null;
};

const normalizeProficiencySummary = (summary) => {
  const armor = summary?.armor || {};
  const weapons = summary?.weapons || {};
  const armorLabels = asArray(armor.labels);
  const weaponLabels = asArray(weapons.labels);

  return {
    armor: {
      ...armor,
      labels: armorLabels.length > 0
        ? armorLabels
        : [armor.light ? 'Light Armor' : '', armor.medium ? 'Medium Armor' : '', armor.heavy ? 'Heavy Armor' : '', armor.shields ? 'Shields' : ''].filter(Boolean)
    },
    weapons: {
      ...weapons,
      labels: weaponLabels.length > 0
        ? weaponLabels
        : [weapons.simple ? 'Simple Weapons' : '', weapons.martial ? 'Martial Weapons' : '', ...asArray(weapons.specific)].filter(Boolean)
    },
    tools: asArray(summary?.tools),
    languages: asArray(summary?.languages)
  };
};

const resolveLoadoutSlot = (item) => {
  const description = `${item?.name || ''} ${item?.type || ''}`.toLowerCase();

  if (/\b(weapons?|shields?|bucklers?|gauntlets?|gloves?|bracers?|bracelets?|wrists?)\b/.test(description)) return 'hands';
  if (/\b(helmets?|helms?|hats?|hoods?|headbands?|circlets?|crowns?|masks?|goggles?|spectacles?|eyes?|amulets?|necklaces?|pendants?|periapts?|medallions?|scarabs?)\b/.test(description)) return 'head';
  if (/\b(armou?rs?|mail|plate|breastplates?|leathers?|hides?|robes?|vests?|shirts?|coats?)\b/.test(description)) return 'body';
  if (/\b(cloaks?|capes?|mantles?|shoulders?|backpacks?|packs?|quivers?)\b/.test(description)) return 'back';
  if (/\b(boots?|shoes?|slippers?|sandals?|greaves?|foot|feet)\b/.test(description)) return 'feet';
  return 'waist';
};

/**
 * Builds an ink-light paper-doll summary without imposing body-slot rules on the character.
 * date_of_change: 2026-08-03
 * rule_reference: Equipped and attuned state comes from canonical inventory data; name-based
 * placement is presentation-only and leaves every location writable when no item is present.
 */
export const buildCharacterEquippedLoadout = (inventoryRows, weaponRows) => {
  const slotItems = new Map(LOADOUT_SLOT_DEFINITIONS.map((slot) => [slot.key, []]));
  const seen = new Set();

  const addItem = (item, preferredSlot) => {
    const name = firstText(item?.name);
    if (!name) return;
    const fingerprint = name.toLowerCase();
    if (seen.has(fingerprint)) return;
    seen.add(fingerprint);
    const slotKey = preferredSlot || resolveLoadoutSlot(item);
    slotItems.get(slotKey)?.push({
      key: firstText(item?.key, `${slotKey}-${fingerprint}`),
      name,
      attuned: item?.attuned === true
    });
  };

  asArray(inventoryRows)
    .filter((item) => item?.equipped === true)
    .forEach((item) => addItem(
      item,
      LOADOUT_SLOT_DEFINITIONS.some((slot) => slot.key === item?.printLocation)
        ? item.printLocation
        : undefined
    ));
  asArray(weaponRows).forEach((weapon) => addItem(weapon, 'hands'));

  const attunedItems = asArray(inventoryRows)
    .filter((item) => item?.attuned === true)
    .map((item) => ({ key: item.key, name: item.name }))
    .filter((item, index, rows) => rows.findIndex((candidate) => candidate.name.toLowerCase() === item.name.toLowerCase()) === index);

  return {
    slots: LOADOUT_SLOT_DEFINITIONS.map((slot) => ({
      ...slot,
      items: slotItems.get(slot.key) || []
    })),
    equippedCount: seen.size,
    attunedItems,
    hasItems: seen.size > 0
  };
};

const buildNarrativeGroups = (character) => {
  const biography = character?.biography && typeof character.biography === 'object'
    ? character.biography
    : {};
  const appearance = character?.appearance && typeof character.appearance === 'object'
    ? character.appearance
    : {};
  const relationshipList = asArray(character?.relationships)
    .map((entry) => typeof entry === 'string' ? entry : firstText(entry?.name, entry?.description, entry?.notes))
    .filter(Boolean)
    .join('\n');

  return [
    makeNarrativeGroup('Background and Personality', [
      makeNarrativeField('Personality Traits', character?.personalityTraits, biography?.personalityTraits),
      makeNarrativeField('Motivations', character?.motivations, biography?.motivations),
      makeNarrativeField('Ideals', character?.ideals, biography?.ideals),
      makeNarrativeField('Bonds', character?.bonds, biography?.bonds),
      makeNarrativeField('Flaws', character?.flaws, biography?.flaws),
      makeNarrativeField('Backstory', character?.backstory, biography?.backstory, biography?.description)
    ]),
    makeNarrativeGroup('Physical Description', [
      makeNarrativeField('Appearance', character?.appearance?.description, typeof character?.appearance === 'string' ? character.appearance : '', biography?.appearance, biography?.description),
      makeNarrativeField('Identifying Features', character?.identifyingFeatures, appearance?.identifyingFeatures)
    ]),
    makeNarrativeGroup('Relationships', [
      makeNarrativeField('Allies and Friends', character?.allies, relationshipList),
      makeNarrativeField('Foes and Enemies', character?.foes),
      makeNarrativeField('Family', character?.family)
    ]),
    makeNarrativeGroup('Political and Social Standing', [
      makeNarrativeField('Deeds and Titles', character?.deedsAndTitles),
      makeNarrativeField('Political Influence', character?.politicalInfluence),
      makeNarrativeField('Political Knowledge', character?.politicalKnowledge),
      makeNarrativeField('Political Preferences', character?.politicalPreferences)
    ]),
    makeNarrativeGroup('Storage and Notes', [
      makeNarrativeField('Equipment in Storage', character?.storageEquipment, typeof character?.storage === 'string' ? character.storage : '')
    ])
  ].filter(Boolean);
};

/**
 * Builds an immutable, print-only projection from the editor's current canonical character.
 * date_of_change: 2026-08-02
 * rule_reference: Printed character records use canonical values directly; print layout state
 * must never write to, normalize, or silently repair the character being printed.
 */
export const buildCharacterPrintModel = (character, options = {}) => {
  const source = character && typeof character === 'object' ? character : {};
  const proficiencyBonus = getCharacterProficiencyBonus(source);
  const abilities = ABILITY_GROUPS.map((ability) => {
    const score = toFiniteNumber(source?.abilities?.[ability.key], 10);
    return {
      ...ability,
      score,
      modifier: getCharacterAbilityModifier(score),
      savingThrow: resolveSavingThrow(source, ability.key),
      skills: ability.skills.map((skill) => resolveCharacterPrintSkill(source, skill, ability.key))
    };
  });
  const inventoryRows = buildCharacterInventoryRows(source);
  const equipment = inventoryRows.filter((item) => !item.magical);
  const magicItems = inventoryRows.filter((item) => item.magical);
  const weaponRows = buildCharacterWeaponTableEntries(source);
  const equippedLoadout = buildCharacterEquippedLoadout(inventoryRows, weaponRows);
  const spellRows = normalizeSpellRows(options.spellData, source);
  const classFeatures = enrichReferenceMechanics(
    normalizeReferenceList(source?.classFeatures, 'Class Feature'),
    source
  );
  const classFeatureNames = new Set(classFeatures.map((entry) => entry.name.toLowerCase()));
  const features = enrichReferenceMechanics(
    normalizeReferenceList(source?.features, 'Feature'),
    source
  ).filter((entry) => !classFeatureNames.has(entry.name.toLowerCase()));
  const speciesTraits = normalizeReferenceList(source?.speciesTraits, 'Species Trait');
  const feats = normalizeReferenceList(source?.feats, 'Feat');
  const classFeatureNotes = hasDistinctStructuredNotes(
    [...classFeatures, ...features],
    source?.classFeaturesText || ''
  ) ? firstText(source?.classFeaturesText) : '';
  const speciesTraitNotes = hasDistinctStructuredNotes(
    speciesTraits,
    source?.speciesTraitsText || source?.racialTraits || ''
  ) ? firstText(source?.speciesTraitsText, source?.racialTraits) : '';
  const featNotes = hasDistinctStructuredNotes(feats, source?.featsText || '')
    ? firstText(source?.featsText)
    : '';
  const wealth = buildCharacterWealthSummary(source);
  const actionEconomy = options.actionEconomyViewModel || buildCharacterActionEconomyViewModel(source);
  const level = Math.max(1, toFiniteNumber(source?.level, 1));
  const species = firstText(source?.species, source?.race);
  const currentHp = toFiniteNumber(source?.hp ?? source?.hitPoints?.current, 0);
  const maxHp = toFiniteNumber(source?.maxHp ?? source?.hitPoints?.maximum ?? source?.hitPoints?.max, currentHp);
  const hitDice = resolveCharacterHitDice(source);
  const armorClass = resolveCharacterArmorClass(source);
  const defensesAndSenses = resolveCharacterDefensesAndSenses(source);
  const choices = resolveCharacterChoiceSummary(source);
  const spellcastingAbilityKey = String(options.spellData?.ability || '').trim().toLowerCase();
  const spellcastingModifier = spellcastingAbilityKey
    ? getCharacterAbilityModifier(source?.abilities?.[spellcastingAbilityKey])
    : null;
  const spellAttackBonus = toFiniteNumber(
    options.spellData?.attackBonus,
    spellcastingModifier != null ? spellcastingModifier + proficiencyBonus : null
  );
  const spellSaveDC = toFiniteNumber(
    options.spellData?.saveDC,
    spellcastingModifier != null ? 8 + spellcastingModifier + proficiencyBonus : null
  );

  return {
    id: firstText(source?.id, 'unsaved-character'),
    name: firstText(options.name, source?.name, 'Unnamed Character'),
    level,
    classLine: formatClassLine(source),
    classProgression: normalizeClassProgression(source),
    species,
    background: firstText(source?.background),
    alignment: firstText(source?.alignment),
    experience: toFiniteNumber(source?.experiencePoints ?? source?.experience ?? source?.xp, 0),
    portraitUrl: firstText(options.portraitUrl),
    combat: {
      armorClass: armorClass.value,
      armorClassSource: armorClass.source,
      currentHp,
      maxHp,
      temporaryHp: toFiniteNumber(source?.tempHp ?? source?.temporaryHp ?? source?.hitPoints?.temporary, 0),
      initiative: toFiniteNumber(source?.initiative, getCharacterAbilityModifier(source?.abilities?.dexterity)),
      speed: formatSpeed(source?.speed?.walk ?? source?.speed),
      proficiencyBonus,
      passivePerception: toFiniteNumber(
        source?.passivePerception,
        10 + resolveCharacterPrintSkill(source, 'Perception', 'wisdom').value
      ),
      size: firstText(source?.size, 'Medium'),
      hitDice: hitDice.label,
      hitDiceState: hitDice,
      defenses: defensesAndSenses.defenses,
      senses: defensesAndSenses.senses,
      inspiration: source?.inspiration === true,
      deathSaves: {
        successes: Math.max(0, Math.min(3, toFiniteNumber(source?.deathSaves?.successes, 0))),
        failures: Math.max(0, Math.min(3, toFiniteNumber(source?.deathSaves?.failures, 0)))
      }
    },
    abilities,
    coreWeapons: weaponRows.slice(0, 6),
    additionalWeapons: weaponRows.slice(6),
    proficiencies: normalizeProficiencySummary(buildCharacterProficiencySummary(source)),
    wealth,
    choices,
    actionEconomy,
    featureGroups: [
      { title: 'Class Features', entries: classFeatures, notes: classFeatureNotes },
      { title: 'Features', entries: features, notes: '' },
      { title: 'Species Traits', entries: speciesTraits, notes: speciesTraitNotes },
      { title: 'Feats', entries: feats, notes: featNotes }
    ].filter((group) => group.entries.length > 0 || group.notes),
    spellcasting: {
      ability: humanize(options.spellData?.ability),
      attackBonus: spellAttackBonus,
      saveDC: spellSaveDC,
      slots: normalizeSpellSlots(options.spellData),
      spells: spellRows
    },
    equipment,
    magicItems,
    equippedLoadout,
    narrativeGroups: buildNarrativeGroups(source),
    minions: normalizeReferenceList(source?.minions, 'Companion')
  };
};

export default buildCharacterPrintModel;
